import { Currency, TradeItemDto } from "@legacy-megarax/crm-contracts";
import { useValidatorForm } from "@legacy-megarax/react-client";
import {
  AsyncButton,
  DateTimeInput,
  DecimalInput,
  NewRouterResponsiveSelect,
  SwitchInput,
  TextInput,
} from "@legacy-megarax/ui-components";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import React, { useEffect, useRef, useState } from "react";

import { formValidator, getErrorMessage, INITIAL_VALUE, ProposalInput } from "./newProposalForm";

interface Props {
  onDialogClose: () => void;
  onCreateProposal: (input: ProposalInput) => Promise<void>;
  getTradeItemOptions: (search: string) => Promise<TradeItemDto[]>;
  currency: Currency;
}

export const NewProposalDialog: React.FC<Props> = ({
  onDialogClose,
  onCreateProposal,
  getTradeItemOptions,
  currency,
}) => {
  const classes = useStyles();
  const [activeImmediately, setActiveImmediately] = useState<boolean>(true);
  const { formValue, onFormInput, onSubmit, formErrors } = useValidatorForm(
    INITIAL_VALUE,
    formValidator,
    onCreateProposal,
  );
  const lastDate = useRef(formValue.activeFrom);

  useEffect(() => {
    if (activeImmediately) return onFormInput("activeFrom")(null);
    return onFormInput("activeFrom")(lastDate.current);
  }, [activeImmediately]);

  useEffect(() => {
    if (formValue.activeFrom) lastDate.current = formValue.activeFrom;
  }, [formValue.activeFrom]);

  return (
    <Dialog open={true} onClose={onDialogClose}>
      <DialogTitle>Nowa propozycja</DialogTitle>
      <DialogContent className={classes.container}>
        <SwitchInput setValue={setActiveImmediately} value={activeImmediately} label="Aktywna od razu?" />
        <DateTimeInput
          label={`Aktywna od ${activeImmediately ? "razu" : ""}`}
          inputVariant="outlined"
          value={formValue.activeFrom}
          onChange={onFormInput("activeFrom")}
          error={getErrorMessage(formErrors.activeFrom)}
          minDate={new Date()}
          ampm={false}
          disabled={activeImmediately}
        />
        <DecimalInput
          label={`Cena netto (${currency})`}
          variant="outlined"
          value={formValue.netPrice}
          onChange={onFormInput("netPrice")}
          error={getErrorMessage(formErrors.netPrice)}
        />
        <NewRouterResponsiveSelect
          getOptions={getTradeItemOptions}
          getKey={(op) => op.gtin}
          getValue={(op) => op.gtin}
          getLabel={(op) => op.itemName}
          label={"Jednostka handlowa"}
          value={formValue.gtin}
          onChange={onFormInput("gtin")}
          error={getErrorMessage(formErrors.gtin)}
        />

        <TextInput
          label={"Komentarz"}
          variant="outlined"
          value={formValue.comment}
          onChange={onFormInput("comment")}
          multiline
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onDialogClose}>Anuluj</Button>
        <AsyncButton color="primary" asyncAction={onSubmit}>
          Utwórz
        </AsyncButton>
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles((theme) => {
  return {
    actions: {
      justifyContent: "space-between",
    },
    container: {
      marginTop: theme.spacing(2),
      display: "flex",
      flexDirection: "column",
      "& > .MuiFormControl-root": {
        margin: theme.spacing(1, 0),
      },
    },
  };
});
